import React, { useState, useEffect } from "react";
import { createItems, getAllItems } from "../../actions/itemActions";
import {
  getAllCategories,
  createCategory,
} from "../../actions/categoryActions";
import { getAllUnits } from "../../actions/unitActions";
import { useDispatch, useSelector } from "react-redux";
import * as xlsx from "xlsx";
import { useSnackbar } from "react-simple-snackbar";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Input from "@material-ui/core/Input";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import IconButton from "@material-ui/core/IconButton";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  form: {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  button: {
    margin: theme.spacing(1),
  },
  input: {
    display: "none",
  },
  fileName: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  clearButton: {
    position: "absolute",
    right: 2,
  },
  requiredField: {
    color: theme.palette.error.main, // Red color from Material-UI
    fontWeight: "bold", // Adds emphasis
  },
}));

const FileUpload = ({ setOpen, open }) => {
  const classes = useStyles();
  const { categories } = useSelector((state) => state.categories);
  const { units } = useSelector((state) => state.units);
  // CHANGE 1: Added rawJson state to store unprocessed Excel data
  const [rawJson, setRawJson] = useState(null);
  const [modifiedJson, setModifiedJson] = useState(null);
  const dispatch = useDispatch();
  const [openSnackbar, closeSnackbar] = useSnackbar();

  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!categories.length) {
      console.log("Fetching categories...");
      dispatch(getAllCategories());
    }
  }, [dispatch, categories]);

  useEffect(() => {
    if (!units.length) {
      console.log("Fetching units...");
      dispatch(getAllUnits());
    }
  }, [dispatch, units]);

  const findCategoryId = (cName) => {
    const filteredCategory = categories.find(
      (c) => c.categoryName.toLowerCase() === cName.toLowerCase()
    );
    console.log(`Finding category "${cName}":`, filteredCategory);
    return filteredCategory ? filteredCategory.id : null;
  };

  const findUnitId = (uName) => {
    const normalizedUName = uName?.toLowerCase().trim();
    const filteredUnit = units.find(
      (unit) => unit.name.toLowerCase() === normalizedUName
    );
    console.log(`Finding unit "${uName}":`, filteredUnit);
    return filteredUnit ? filteredUnit.id : null;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const createNewCategory = async (categoryName) => {
    let existingCategoryId = findCategoryId(categoryName);
    if (existingCategoryId) {
      console.log(
        `Category "${categoryName}" already exists with ID: ${existingCategoryId}`
      );
      return existingCategoryId;
    }

    const newCategory = { categoryName, isActive: true, isDeleted: false };
    try {
      console.log(`Creating new category "${categoryName}"...`);
      const newCategoryData = await dispatch(createCategory(newCategory));
      console.log("Created category response:", newCategoryData);

      if (!newCategoryData || !newCategoryData.id) {
        throw new Error("Failed to get valid category data from response");
      }

      const newCategoryId = newCategoryData.id;
      console.log(
        `Successfully created category "${categoryName}" with ID: ${newCategoryId}`
      );
      openSnackbar(`Category "${categoryName}" created successfully`);
      return newCategoryId;
    } catch (error) {
      console.error(`Failed to create category "${categoryName}":`, error);
      openSnackbar(
        `Failed to create category "${categoryName}": ${error.message}`
      );
      const fallbackId = findCategoryId(categoryName);
      if (fallbackId) {
        console.log(
          `Category "${categoryName}" found after error with ID: ${fallbackId}`
        );
        return fallbackId;
      }
      return null;
    }
  };

  // CHANGE 2: Updated readUploadFile to only read the file and store raw JSON
  const readUploadFile = (e) => {
    e.preventDefault();
    if (!e.target.files) {
      console.log("No file selected");
      openSnackbar("No file selected");
      return;
    }

    const file = e.target.files[0];
    setSelectedFile(file);
    console.log("Selected file:", file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        console.log("Raw Excel data:", json);
        setRawJson(json); // Store raw data instead of processing it here
      } catch (error) {
        console.error("Error reading file:", error);
        openSnackbar("Failed to read the uploaded file: " + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // CHANGE 3: Updated createItemData to handle all processing logic
  const createItemData = async () => {
    if (!rawJson || rawJson.length === 0) {
      console.log("No valid file uploaded");
      openSnackbar("No valid file uploaded");
      return;
    }

    try {
      // Ensure units and categories are loaded initially
      if (!units.length || !categories.length) {
        console.warn(
          "Units or categories not loaded yet. Fetching them now..."
        );
        await Promise.all([
          !units.length && dispatch(getAllUnits()),
          !categories.length && dispatch(getAllCategories()),
        ]);
      }
      console.log("Initial categories state:", categories);

      // Step 1: Create all categories and store their IDs in a map
      const uniqueCategoryNames = [
        ...new Set(rawJson.map((item) => item["Category"] || "Uncategorized")),
      ].filter((categoryName) => categoryName !== "Uncategorized");

      const categoryMap = new Map();
      for (const categoryName of uniqueCategoryNames) {
        let categoryId = findCategoryId(categoryName);
        if (!categoryId) {
          categoryId = await createNewCategory(categoryName);
        }
        if (categoryId) {
          categoryMap.set(categoryName, categoryId);
          console.log(
            `Stored ${categoryName} with ID: ${categoryId} in categoryMap`
          );
        } else {
          console.warn(`Failed to get ID for category "${categoryName}"`);
        }
      }

      // Step 2: Refresh Redux state with all categories
      await dispatch(getAllCategories());
      console.log("Updated categories state after creation:", categories);

      // Step 3: Map Excel data to items
      const skippedItems = []; // Collect skipped items
      const modifiedJson = rawJson.map((item) => {
        if (
          !item.itemName ||
          item["Sale Price"] === undefined ||
          item["Opening Stock"] === undefined ||
          !item["Sale Unit"]
        ) {
          const itemName = item.itemName || "unknown";
          const warningMessage = `Skipping item "${itemName}": Missing required fields`;
          console.warn(warningMessage, item);
          openSnackbar(warningMessage); // Display the warning in the UI
          return null;
        }
        const saleUnitId = findUnitId(item["Sale Unit"]);
        if (!saleUnitId) {
          console.warn(
            `Sale unit "${item["Sale Unit"]}" not found for item "${item.itemName}". Using default unit.`
          );
        }

        const categoryName = item["Category"] || "Uncategorized";
        const categoryId =
          categoryName === "Uncategorized"
            ? null
            : categoryMap.get(categoryName) || findCategoryId(categoryName);

        if (!categoryId && categoryName !== "Uncategorized") {
          console.error(`Category ID missing for "${categoryName}"`, {
            mapValue: categoryMap.get(categoryName),
            stateValue: findCategoryId(categoryName),
            allCategories: categories,
          });
        }

        const defaultUnitId = units.length > 0 ? units[0].id : "";
        const finalSaleUnitId = saleUnitId || defaultUnitId;

        const mappedItem = {
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          itemName: item.itemName,
          units: {
            sale: {
              unit: finalSaleUnitId,
              price: parseFloat(item["Sale Price"]) || 0,
            },
            purchase: {
              unit: item["Purchase Unit"]
                ? findUnitId(item["Purchase Unit"])
                : null,
              cost: parseFloat(item["Purchase Cost"]) || 0,
            },
            stock: {
              unit: item["Stock Unit"]
                ? findUnitId(item["Stock Unit"])
                : finalSaleUnitId,
              quantity: parseFloat(item["Opening Stock"]) || 0,
            },
          },
          category: {
            categoryName: categoryName,
            categoryId: categoryId,
          },
          toggleStock:
            item["Toggle Stock"] === "Yes" ||
            parseFloat(item["Opening Stock"]) > 0,
          barcode: item["Barcode"] || undefined,
          vat: parseFloat(item["VAT"]) || 0,
          stockQty: {
            initialQty: parseFloat(item["Opening Stock"]) || 0,
            currentQty: parseFloat(item["Opening Stock"]) || 0,
            minQty: parseFloat(item["Min Qty"]) || 0,
            maxQty: parseFloat(item["Max Qty"]) || 0,
          },
        };
        console.log("Mapped item:", mappedItem);
        return mappedItem;
      });

      // Display all skipped items in one snackbar after mapping
      if (skippedItems.length > 0) {
        openSnackbar(
          `Skipped ${skippedItems.length} items: ${skippedItems.join(", ")}`
        );
      }

      const validItems = modifiedJson.filter((item) => item !== null);
      console.log("Final modifiedJson:", validItems);

      if (validItems.length === 0) {
        openSnackbar("No valid items found in the uploaded file");
        return;
      }

      setModifiedJson(validItems);

      // Step 4: Dispatch items to backend
      console.log("Dispatching createItems with:", validItems);
      await dispatch(createItems(validItems, openSnackbar));
      console.log("Items imported successfully");

      dispatch(getAllItems()); // Refresh the items list
      openSnackbar("Items successfully added!");
      handleClose();
    } catch (error) {
      console.error("Failed to import items:", error);
      openSnackbar("Failed to import items: " + error.message);
    }
  };

  const downloadSampleFile = () => {
    const headers = [
      "itemName",
      "Sale Unit",
      "Sale Price",
      "Opening Stock",
      "Category",
      "Stock Unit",
      "Toggle Stock",
      "Min Qty",
      "Max Qty",
    ];

    const sampleData = [
      headers,
      [
        "Aashirwad Atta 2kg",
        "Kilogram",
        120,
        50,
        "ATTA",
        "Kilogram",
        "Yes",
        10,
        100,
      ],
      ["Sugar", "Kilogram", 40, 10, "Groceries", "Kilogram", "No", 5, 100],
    ];

    const ws = xlsx.utils.aoa_to_sheet(sampleData);
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "Sample");
    xlsx.writeFile(wb, "sample_items.xlsx");
  };

  const clearFile = () => {
    setSelectedFile(null);
    setRawJson(null); // CHANGE 4: Clear rawJson as well
    setModifiedJson(null);
    document.getElementById("upload").value = null;
  };

  return (
    <div className={classes.container}>
      <div>
        {selectedFile ? (
          <>
            <span className={classes.fileName}>{selectedFile.name}</span>
            <IconButton
              className={classes.clearButton}
              onClick={clearFile}
              aria-label="Clear"
            >
              <ClearIcon />
            </IconButton>
            <Button
              variant="contained"
              color="secondary"
              className={classes.button}
              startIcon={<CloudDownloadIcon />}
              onClick={createItemData}
            >
              Import File
            </Button>
          </>
        ) : (
          <FormControl component="form" className={classes.form}>
            <FormLabel htmlFor="upload" style={{ cursor: "pointer" }}>
              Upload File
            </FormLabel>
            <Input
              type="file"
              name="upload"
              id="upload"
              className={classes.input}
              onChange={readUploadFile}
            />
            <IconButton
              className={classes.button}
              color="primary"
              aria-label="Upload"
              component="span"
              onClick={() => document.getElementById("upload").click()}
            >
              <CloudUploadIcon />
            </IconButton>
          </FormControl>
        )}
        {!selectedFile && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              startIcon={<CloudDownloadIcon />}
              onClick={downloadSampleFile}
            >
              Download Sample File
            </Button>
            <Typography variant="body1" color="initial">
              Required fields:{" "}
              <span className={classes.requiredField}>Item Name</span>,{" "}
              <span className={classes.requiredField}>Sale Unit</span>,{" "}
              <span className={classes.requiredField}>Sale Price</span>,{" "}
              <span className={classes.requiredField}>Opening Stock</span>
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
